import React, { useState } from 'react'
import { Box, Menu, MenuItem, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { ToolbarIconButton } from '@/design-system'
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew'
import UndoIcon from '@mui/icons-material/UndoOutlined'
import RedoIcon from '@mui/icons-material/RedoOutlined'
import SaveIcon from '@mui/icons-material/CheckCircleOutline'
import ShareIcon from '@mui/icons-material/IosShareOutlined'
import LockIcon from '@mui/icons-material/LockOutlined'
import PublicIcon from '@mui/icons-material/PublicOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'

export interface StudioTopBarProps {
  title: string
  isPrivate: boolean
  isOwner: boolean
  changesCount: number
  saving: boolean
  onBack: () => void
  onUndo: () => void
  onRedo: () => void
  onSave: () => void
  onTogglePrivacy: () => void
  onShare: () => void
  onScreenshot: () => void
  onRename: () => void
  onDuplicate: () => void
  onDelete: () => void
}

const Root = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 64,
  flexShrink: 0,
  padding: '0 var(--ds-space-4)',
  borderBottom: '1px solid var(--ds-border)',
  backgroundColor: 'var(--ds-surface)',
  zIndex: 5,
})

const Side = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--ds-space-2)',
  minWidth: 0,
})

const Title = styled(Typography)({
  fontFamily: 'var(--ds-font-display)',
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: 'var(--ds-ink)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 240,
  cursor: 'pointer',
})

/** Top chrome bar for the gallery studio — back, title, history, save, privacy, share */
export function StudioTopBar({
  title,
  isPrivate,
  isOwner,
  changesCount,
  saving,
  onBack,
  onUndo,
  onRedo,
  onSave,
  onTogglePrivacy,
  onShare,
  onScreenshot,
  onRename,
  onDuplicate,
  onDelete,
}: StudioTopBarProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  return (
    <Root>
      <Side>
        <ToolbarIconButton icon={<ArrowBackIcon sx={{ fontSize: 17 }} />} label='Back' onClick={onBack} />
        <Title onClick={isOwner ? onRename : undefined} title={isOwner ? 'Rename' : title}>
          {title}
        </Title>
      </Side>

      <Side>
        <ToolbarIconButton icon={<UndoIcon />} label='Undo' onClick={onUndo} />
        <ToolbarIconButton icon={<RedoIcon />} label='Redo' onClick={onRedo} />

        {isOwner && (
          <ToolbarIconButton
            icon={isPrivate ? <LockIcon /> : <PublicIcon />}
            label={isPrivate ? 'Private — click to make public' : 'Public — click to make private'}
            onClick={onTogglePrivacy}
          />
        )}

        <ToolbarIconButton icon={<CameraAltOutlinedIcon />} label='Take screenshot' onClick={onScreenshot} />
        <ToolbarIconButton icon={<ShareIcon />} label='Share' onClick={onShare} />

        {isOwner && (
          <ToolbarIconButton
            icon={<SaveIcon />}
            label='Save'
            badge={changesCount}
            active
            disabled={saving}
            onClick={onSave}
          />
        )}

        {isOwner && (
          <>
            <ToolbarIconButton
              icon={<MoreHorizIcon />}
              label='More'
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            />
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  onRename()
                }}
              >
                Rename
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  onDuplicate()
                }}
              >
                Duplicate
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  onDelete()
                }}
                sx={{ color: 'var(--ds-danger)' }}
              >
                Delete
              </MenuItem>
            </Menu>
          </>
        )}
      </Side>
    </Root>
  )
}
